import { inject, Injectable, WritableSignal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { TNil } from '@app/app.interface';
import { ICtaForm, ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import { CampaignDatesService } from '@campaign-form/form-fields/campaign-basics/campaign-dates/campaign-dates.service';
import { CtaSelectService } from '@campaign-form/form-fields/creators-and-ctas/cta-select.service';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IGamePlayDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import {
  EExecutionType,
  IExecutionForm,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { ALL_CREATORS_ID } from '@shared/constants/all-creators.constants';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { differenceInMinutes, isAfter, isBefore, max, min } from 'date-fns';
import { combineLatest, take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CreatorsAndCtasService } from '../creators-and-ctas.service';

@Injectable({
  providedIn: 'root',
})
export class DeliverableFlowDialogService {
  private readonly creatorsAndCtasService = inject(CreatorsAndCtasService);
  private readonly campaignDatesService = inject(CampaignDatesService);
  private readonly dateUtilitiesService = inject(DateUtilitiesService);

  private initialCtaValues: FormGroup<ICtaForm>['value'] | TNil = null;

  public createCtaForm(
    selectedPlatformFeature: EPlatformFeature | TNil,
    ctaValues: FormGroup<ICtaForm>['value'] | TNil,
    ctaForm: WritableSignal<FormGroup<ICtaForm> | TNil>,
    creatorIds: string[],
    selectedCreatorIds: Set<string>,
  ): void {
    const platformFeature = selectedPlatformFeature || ctaValues?.platformFeature;

    if (!platformFeature || ctaForm()) {
      return;
    }

    // Only cleared when a fresh form is actually created - the dialog's effect re-runs on every
    // creator-selection change with an existing form (early return above), and clearing here would
    // null the discard-restore source (getInitialCtaValues) mid-session, silently dropping the
    // "Discard changes" restore on close.
    this.clearInitialCtaValues();

    const ctaOption = CtaSelectService.allCtaOptions[platformFeature];

    if (ctaValues) {
      this.copyInitialCtaValues(ctaValues);
      ctaForm.set(this.creatorsAndCtasService.createCtaFormFromCtaValues(ctaValues));
      ctaForm()?.controls?.isAllCreators?.setValue(
        creatorIds.includes(ALL_CREATORS_ID) || selectedCreatorIds.has(ALL_CREATORS_ID),
      );
    } else {
      ctaForm.set(
        new FormGroup<ICtaForm>({
          customCtaValues: new FormControl<ICustomCtaValue[]>([], { nonNullable: true }),
          deliverables: new FormArray<FormGroup<TDeliverableForm>>([]),
          exclusiveTo: new FormControl(null),
          executions: new FormArray<FormGroup<IExecutionForm>>([]),
          icon: new FormControl(ctaOption.icon),
          id: new FormControl(uuidv4()),
          isAllCreators: new FormControl(
            creatorIds.includes(ALL_CREATORS_ID) || selectedCreatorIds.has(ALL_CREATORS_ID),
          ),
          isCtaDetailsValid: new FormControl(true),
          isCustomMode: new FormControl(false),
          name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
          platformFeature: new FormControl(platformFeature, {
            nonNullable: true,
          }),
        }),
      );
    }

    setTimeout(() => {
      ctaForm()?.controls?.name?.setErrors(null);
    });
  }

  public getInitialCtaValues(): FormGroup<ICtaForm>['value'] | TNil {
    return this.initialCtaValues;
  }

  public normalizeGlobalTemplateTimings(ctaForm: FormGroup<ICtaForm> | TNil): void {
    if (!ctaForm) {
      return;
    }

    combineLatest([this.campaignDatesService.endDate$, this.campaignDatesService.startDate$])
      .pipe(take(1))
      .subscribe(([campaignEndDate, campaignStartDate]) => {
        if (
          !campaignEndDate ||
          !campaignStartDate ||
          isAfter(campaignStartDate, campaignEndDate) ||
          campaignStartDate.getTime() === campaignEndDate.getTime()
        ) {
          return;
        }

        const allowedExecutionDurations = this.normalizeExecutionDurations(ctaForm, campaignEndDate, campaignStartDate);

        this.normalizeGamePlayDurations(ctaForm, allowedExecutionDurations, campaignEndDate, campaignStartDate);
        ctaForm.updateValueAndValidity({ emitEvent: false });
      });
  }

  private normalizeExecutionDurations(
    ctaForm: FormGroup<ICtaForm>,
    campaignEndDate: Date,
    campaignStartDate: Date,
  ): number[] {
    const allowedExecutionDurations: number[] = [];
    const executionControls = ctaForm.controls.executions.controls as FormGroup<IExecutionForm>[];

    executionControls.forEach((executionControl) => {
      const executionType = executionControl.controls.executionType.value;

      if (executionType === EExecutionType.timeFrame) {
        this.normalizeTimeFrameWindow(executionControl, campaignEndDate, campaignStartDate);
      }

      if (
        executionType !== EExecutionType.unique &&
        executionType !== EExecutionType.undesignated &&
        executionType !== EExecutionType.timeFrame
      ) {
        return;
      }

      const durationStartDate = this.resolveExecutionDurationStartDate(
        campaignEndDate,
        campaignStartDate,
        executionControl,
        executionType,
      );
      const durationControl = executionControl.controls.duration;
      const duration = this.parseDurationValue(durationControl.value);

      if (duration === null) {
        executionControl.updateValueAndValidity({ emitEvent: false });

        return;
      }

      const remainingMinutes = this.getDurationInMinutes(durationStartDate, campaignEndDate);
      const normalizedDuration = this.normalizeDuration(Math.min(duration, remainingMinutes));

      if (normalizedDuration !== duration) {
        durationControl.setValue(normalizedDuration, { emitEvent: false });
      }

      executionControl.updateValueAndValidity({ emitEvent: false });
      allowedExecutionDurations.push(normalizedDuration);
    });

    return allowedExecutionDurations;
  }

  private normalizeGamePlayDurations(
    ctaForm: FormGroup<ICtaForm>,
    allowedExecutionDurations: number[],
    campaignEndDate: Date,
    campaignStartDate: Date,
  ): void {
    const defaultGamePlayDurationLimit = this.getDurationInMinutes(campaignStartDate, campaignEndDate);
    const maximumGamePlayDuration = allowedExecutionDurations.length
      ? Math.min(...allowedExecutionDurations)
      : defaultGamePlayDurationLimit;
    const gamePlayControls = ctaForm.controls.deliverables.controls.filter(
      (deliverableControl: FormGroup<TDeliverableForm>) =>
        deliverableControl.controls.type.value === EDeliverableType.gamePlay,
    ) as FormGroup<IGamePlayDeliverableForm>[];

    gamePlayControls.forEach((gamePlayControl) => {
      const durationControl = gamePlayControl.controls.duration;
      const duration = this.parseDurationValue(durationControl.value);

      if (duration === null) {
        gamePlayControl.updateValueAndValidity({ emitEvent: false });

        return;
      }

      const normalizedDuration = this.normalizeDuration(Math.min(duration, maximumGamePlayDuration));

      if (normalizedDuration !== duration) {
        durationControl.setValue(normalizedDuration, { emitEvent: false });
      }

      gamePlayControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private resolveExecutionDurationStartDate(
    campaignEndDate: Date,
    campaignStartDate: Date,
    executionControl: FormGroup<IExecutionForm>,
    executionType: EExecutionType | null,
  ): Date {
    if (executionType !== EExecutionType.unique) {
      return campaignStartDate;
    }

    const executionDate = executionControl.controls.executionDate.value;
    const executionTime = executionControl.controls.executionTime.value;

    if (!executionDate) {
      return campaignStartDate;
    }

    const executionTimestamp = this.dateUtilitiesService.combineDateTime(executionDate, executionTime);
    const isExecutionOutsideCampaignRange =
      isBefore(executionTimestamp, campaignStartDate) || isAfter(executionTimestamp, campaignEndDate);

    if (isExecutionOutsideCampaignRange) {
      executionControl.controls.executionDate.setValue(campaignStartDate, { emitEvent: false });
      executionControl.controls.executionTime.setValue(campaignStartDate, { emitEvent: false });

      return campaignStartDate;
    }

    return executionTimestamp;
  }

  // A global template's window holds absolute dates from wherever it was authored - fit it into this
  // campaign, falling back to the whole campaign when it doesn't overlap at all.
  private normalizeTimeFrameWindow(
    executionControl: FormGroup<IExecutionForm>,
    campaignEndDate: Date,
    campaignStartDate: Date,
  ): void {
    const { executionEndDate, executionStartDate } = executionControl.controls;

    if (!executionStartDate.value || !executionEndDate.value) {
      return;
    }

    let start = max([executionStartDate.value, campaignStartDate]);
    let end = min([executionEndDate.value, campaignEndDate]);

    // start = end is a valid (zero-length) window - but only when the template's window is one too. A real
    // window that merely touches the campaign would shrink to an instant.
    const isOutsideCampaign =
      isAfter(start, end) || (!isBefore(start, end) && isBefore(executionStartDate.value, executionEndDate.value));

    if (isOutsideCampaign) {
      start = campaignStartDate;
      end = campaignEndDate;
    }

    executionStartDate.setValue(start, { emitEvent: false });
    executionEndDate.setValue(end, { emitEvent: false });
  }

  private getDurationInMinutes(startDate: Date, endDate: Date): number {
    const durationInMinutes = differenceInMinutes(endDate, startDate);

    return Math.max(durationInMinutes, 0);
  }

  private normalizeDuration(duration: number): number {
    const roundedDuration = Math.floor(Math.max(duration, 0) * 100) / 100;

    return roundedDuration;
  }

  private parseDurationValue(durationValue: number | string | null | undefined): number | null {
    if (durationValue === '' || durationValue === null || durationValue === undefined) {
      return null;
    }

    const parsedDurationValue = parseFloat(durationValue.toString());

    if (Number.isNaN(parsedDurationValue)) {
      return null;
    }

    return parsedDurationValue;
  }

  private copyInitialCtaValues(ctaValues: FormGroup<ICtaForm>['value']): void {
    this.initialCtaValues = structuredClone(ctaValues);
  }

  private clearInitialCtaValues(): void {
    this.initialCtaValues = null;
  }
}
